import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { canPreviewFile, fileKindLabel } from './fileKinds';

/*
 * Presentational cross-client document browser, shared by the employee Work
 * page (/work/documents) and the B2B firm surface (/entrepreneur). Deliberately
 * store-agnostic: it owns only view state (which tab, expanded folders, search
 * draft) and reports filter changes up via `onParamsChange`. All data fetching
 * (RTK on the employee side, b2bFetch on the B2B side) and downloads live in the
 * parent, so this file has no dependency on Redux/MSAL and can move with the B2B
 * bundle if it ever splits out.
 */

const work = {
  deepBlue: '#0a2947', tealBlue: '#0069a6', goldenrod: '#d09b2c', ink: '#0a2947',
  muted: '#6b7280', hairline: '#dde2e8', hairlineStrong: '#c4cbd4',
  surface: '#ffffff', canvas: '#f3f5f8', green: '#1a7f4e', red: '#b00020',
};

const WORKFLOW_LABELS = {
  discovery: 'Discovery', design: 'Design', delivery: 'Delivery', dedicate: 'Dedicate',
};
const STATUS_LABELS = { uploaded: 'Received', pending: 'Pending' };

const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

const iconForType = (contentType, filename) => {
  const s = `${contentType || ''} ${filename || ''}`.toLowerCase();
  if (s.includes('pdf')) return 'file-earmark-pdf';
  if (/(png|jpg|jpeg|heic|webp|image)/.test(s)) return 'file-earmark-image';
  if (/(xls|xlsx|csv|sheet)/.test(s)) return 'file-earmark-spreadsheet';
  if (/(doc|docx|word)/.test(s)) return 'file-earmark-word';
  return 'file-earmark-text';
};

function selectStyle() {
  return {
    border: `1px solid ${work.hairline}`, borderRadius: 6, padding: '0.4rem 0.55rem',
    fontSize: '0.82rem', color: work.ink, background: work.surface, cursor: 'pointer',
    maxWidth: 200,
  };
}

function Chip({ icon, label, value }) {
  if (!value) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: work.canvas, border: `1px solid ${work.hairline}`, borderRadius: 999, padding: '0.1rem 0.5rem', fontSize: '0.72rem', color: work.muted, whiteSpace: 'nowrap' }}>
      {icon && <i className={`bi bi-${icon}`} />}
      {label && <span style={{ fontWeight: 700, color: work.deepBlue }}>{label}</span>}
      <span>{value}</span>
    </span>
  );
}
Chip.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node };

function StatusPill({ status }) {
  const received = status === 'uploaded';
  return (
    <span style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: received ? work.green : work.goldenrod, whiteSpace: 'nowrap' }}>
      <i className={`bi bi-${received ? 'check-circle-fill' : 'hourglass-split'} me-1`} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}
StatusPill.propTypes = { status: PropTypes.string };

// View (opens the file in the in-app viewer) + Download (icon only) actions,
// shared by the list row and the folder-tree leaf. `onView` and `onDownload`
// are both optional — each button is hidden when the surface doesn't provide
// a handler (onDownload is omitted when the admin config's
// show_download_buttons flag is off).
function FileActions({ doc, onDownload, onView }) {
  // Office formats, CSV and HEIC can't render in the viewer — handed to its
  // iframe they download instead — so View is replaced by a note saying so
  // rather than left promising a preview that never appears. Download (below)
  // is unaffected and does the same job honestly. One change here covers both
  // the list row and the folder-tree leaf, on the Work page and in the B2B
  // firm's tab.
  const previewable = canPreviewFile(doc.original_filename || doc.filename);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
      {onView && previewable && (
        <button
          type="button"
          onClick={() => onView(doc)}
          title="Open this file"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: work.surface, color: work.tealBlue, border: `1px solid ${work.tealBlue}`, borderRadius: 6, padding: '0.3rem 0.55rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          <i className="bi bi-eye" />View
        </button>
      )}
      {!previewable && (
        <span style={{ color: work.muted, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
          {fileKindLabel(doc.original_filename || doc.filename)} - downloads
        </span>
      )}
      {onDownload && (
        <button
          type="button"
          onClick={() => onDownload(doc)}
          title="Download this file"
          aria-label="Download this file"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: work.tealBlue, color: 'white', border: 'none', borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}
        >
          <i className="bi bi-download" />
        </button>
      )}
    </div>
  );
}
FileActions.propTypes = { doc: PropTypes.object.isRequired, onDownload: PropTypes.func, onView: PropTypes.func };

// Advisor-editable "which year is this document about" tag (Work-side only —
// only rendered where the parent passes an onSetYear handler; otherwise shows
// a plain chip when a year happens to already be set, and nothing at all
// when there's neither a year nor a way to add one).
function YearTag({ documentYear, uploadId, onSetYear }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(documentYear ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(documentYear ?? ''); }, [documentYear]);

  if (!onSetYear) {
    return documentYear ? <Chip icon="calendar-range" label="Year" value={documentYear} /> : null;
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Set which year this document is about"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
          background: documentYear ? work.canvas : 'transparent',
          border: `1px dashed ${work.hairlineStrong}`, borderRadius: 999,
          padding: '0.1rem 0.5rem', fontSize: '0.72rem', color: documentYear ? work.deepBlue : work.muted,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <i className="bi bi-calendar-range" />
        {documentYear || 'Set year'}
      </button>
    );
  }

  const save = async () => {
    const trimmed = String(draft).trim();
    const n = trimmed === '' ? null : Number(trimmed);
    if (n !== null && (!Number.isFinite(n) || n < 1900 || n > 2100)) return;
    setSaving(true);
    try {
      await onSetYear(uploadId, n);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}>
      <input
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Year"
        disabled={saving}
        style={{ width: 68, fontSize: '0.75rem', padding: '0.1rem 0.35rem', border: `1px solid ${work.hairlineStrong}`, borderRadius: 4, color: work.ink }}
      />
      <button type="button" onClick={save} disabled={saving} title="Save" style={{ background: work.green, color: 'white', border: 0, borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontSize: '0.7rem' }}>
        <i className="bi bi-check-lg" />
      </button>
      <button type="button" onClick={() => { setEditing(false); setDraft(documentYear ?? ''); }} disabled={saving} title="Cancel" style={{ background: 'transparent', color: work.muted, border: `1px solid ${work.hairlineStrong}`, borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontSize: '0.7rem' }}>
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
}
YearTag.propTypes = { documentYear: PropTypes.number, uploadId: PropTypes.number, onSetYear: PropTypes.func };

// Metadata chips common to list + folder rows (institution/account/holder/…).
function DocMeta({ doc, onSetYear }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem', alignItems: 'center' }}>
      {/* Where this document came from. Only marked when it is NOT a portal
          upload: the list is overwhelmingly client uploads, so badging the
          exception keeps the rows quiet while still making provenance
          obvious - these two corpora are not interchangeable. */}
      {doc.source_system === 'fsc' && (
        <span
          title={doc.fsc_household_name
            ? `On file in Salesforce for ${doc.fsc_household_name}`
            : 'On file in Salesforce'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            background: 'rgba(0,105,166,0.10)', color: work.tealBlue,
            borderRadius: 4, padding: '0.1rem 0.4rem',
            fontSize: '0.68rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap',
          }}
        >
          <i className="bi bi-cloud" />Salesforce
        </span>
      )}
      <Chip icon="bank" value={doc.institution} />
      <Chip label="Type" value={doc.account_type} />
      {doc.account_number_last4 && <Chip label="Acct" value={`…${doc.account_number_last4}`} />}
      <Chip label="Holder" value={doc.account_holder} />
      <Chip label="Balance" value={doc.total_balance} />
      <Chip icon="calendar3" value={doc.statement_period} />
      <YearTag
        documentYear={doc.document_year}
        uploadId={doc.upload_id}
        onSetYear={doc.upload_id ? onSetYear : undefined}
      />
    </div>
  );
}
DocMeta.propTypes = { doc: PropTypes.object.isRequired, onSetYear: PropTypes.func };

// A single document, used by both views.
function DocRow({ doc, onDownload, onView, clientHref, onSetYear }) {
  const name = doc.client_name || '—';
  return (
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.7rem 0.85rem', borderBottom: `1px solid ${work.hairline}` }}>
      <i className={`bi bi-${iconForType(doc.content_type, doc.original_filename)}`} style={{ color: work.tealBlue, fontSize: '1.25rem', marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: work.deepBlue, wordBreak: 'break-word' }}>
            {doc.display_label || doc.doc_label || doc.original_filename}
          </span>
          {doc.doc_label && doc.category_label && (
            <span style={{ fontSize: '0.72rem', color: work.muted }}>{doc.category_label}</span>
          )}
        </div>
        <div style={{ fontSize: '0.78rem', color: work.muted, marginTop: 2 }}>
          {clientHref && clientHref(doc.client_public_id) ? (
            <a href={clientHref(doc.client_public_id)} style={{ color: work.tealBlue, fontWeight: 600, textDecoration: 'none' }}>{name}</a>
          ) : (
            <span style={{ color: work.ink, fontWeight: 600 }}>{name}</span>
          )}
          {doc.firm_name && <span> · {doc.firm_name}</span>}
          {doc.advisor_name && <span> · {doc.advisor_name}</span>}
          {doc.workflow_status && <span> · {WORKFLOW_LABELS[doc.workflow_status] || doc.workflow_status}</span>}
        </div>
        <DocMeta doc={doc} onSetYear={onSetYear} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem', flexShrink: 0 }}>
        <StatusPill status={doc.status} />
        <span style={{ fontSize: '0.72rem', color: work.muted }}>{fmtDate(doc.uploaded_at)}</span>
        <FileActions doc={doc} onDownload={onDownload} onView={onView} />
        {doc.file_size_bytes ? <span style={{ fontSize: '0.68rem', color: work.muted }}>{fmtBytes(doc.file_size_bytes)}</span> : null}
      </div>
    </div>
  );
}
DocRow.propTypes = { doc: PropTypes.object.isRequired, onDownload: PropTypes.func, onView: PropTypes.func, clientHref: PropTypes.func, onSetYear: PropTypes.func };

// A document's stable React key. Portal uploads are keyed by upload_id;
// Salesforce documents have none (they are rows in `documents`, not uploads)
// and are keyed by their doc_id instead.
function docKey(d) {
  return d.upload_id != null ? `f:${d.upload_id}` : `fsc:${d.doc_id}`;
}

// Classic folder-tree drilldown. Documents are nested into a Windows-Explorer-
// style hierarchy the advisors already think in:
//   Client ▸ Category (e.g. Bank Accounts) ▸ Document type (e.g. Checking) ▸ files
// Each folder expands/collapses independently; nested rows are indented under a
// hairline guide so the drilldown reads like the familiar SharePoint/Box tree.
function buildFolderTree(documents) {
  const roots = [];
  const clients = new Map();
  documents.forEach((d) => {
    const clientKey = d.client_public_id || d.client_name || 'unknown';
    let client = clients.get(clientKey);
    if (!client) {
      client = {
        id: `c:${clientKey}`, kind: 'folder', level: 'client',
        name: d.client_name || 'Unknown client',
        firm: d.firm_name, advisor: d.advisor_name, publicId: d.client_public_id,
        _kids: new Map(), children: [], count: 0,
      };
      clients.set(clientKey, client);
      roots.push(client);
    }
    client.count += 1;

    const catKey = d.category || d.category_label || 'other';
    let cat = client._kids.get(catKey);
    if (!cat) {
      cat = {
        id: `${client.id}/cat:${catKey}`, kind: 'folder', level: 'category',
        name: d.category_label || 'Other', _kids: new Map(), children: [], count: 0,
      };
      client._kids.set(catKey, cat);
      client.children.push(cat);
    }
    cat.count += 1;

    const typeKey = d.type_key || d.doc_label || 'doc';
    let type = cat._kids.get(typeKey);
    if (!type) {
      type = {
        id: `${cat.id}/type:${typeKey}`, kind: 'folder', level: 'type',
        name: d.doc_label || d.display_label || 'Document', children: [], count: 0,
      };
      cat._kids.set(typeKey, type);
      cat.children.push(type);
    }
    type.count += 1;
    type.children.push({
      id: docKey(d), kind: 'file',
      name: d.display_label || d.doc_label || d.original_filename, doc: d,
    });
  });

  const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
  const sortRec = (nodes) => {
    nodes.sort(byName);
    nodes.forEach((n) => { if (n.children && n.children.length) sortRec(n.children); });
  };
  sortRec(roots);
  return roots;
}

function collectFolderIds(nodes, acc = []) {
  nodes.forEach((n) => {
    if (n.kind === 'folder') {
      acc.push(n.id);
      collectFolderIds(n.children, acc);
    }
  });
  return acc;
}

// Classic [+]/[−] expander box, like a native tree control.
function TreeToggle({ isOpen }) {
  return (
    <span style={{ width: 15, height: 15, flexShrink: 0, border: `1px solid ${work.hairlineStrong}`, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, lineHeight: 1, fontWeight: 700, color: work.deepBlue, background: work.surface }}>
      {isOpen ? '−' : '+'}
    </span>
  );
}
TreeToggle.propTypes = { isOpen: PropTypes.bool };

function FolderRow({ node, isOpen, onToggle, clientHref }) {
  const folderIcon = isOpen ? 'folder2-open' : 'folder-fill';
  const href = node.level === 'client' && clientHref ? clientHref(node.publicId) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.35rem 0.4rem' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flex: 1, minWidth: 0, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
      >
        <TreeToggle isOpen={isOpen} />
        <i className={`bi bi-${folderIcon}`} style={{ color: work.goldenrod, fontSize: '1.1rem', flexShrink: 0 }} />
        <span style={{ fontWeight: node.level === 'client' ? 800 : 700, color: work.deepBlue, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {node.name}
        </span>
        {node.level === 'client' && node.firm && <span style={{ fontSize: '0.74rem', color: work.muted, whiteSpace: 'nowrap' }}>· {node.firm}</span>}
        {node.level === 'client' && node.advisor && <span style={{ fontSize: '0.74rem', color: work.muted, whiteSpace: 'nowrap' }}>· {node.advisor}</span>}
      </button>
      <span style={{ fontSize: '0.72rem', color: work.muted, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {node.count} file{node.count === 1 ? '' : 's'}
      </span>
      {href && (
        <a href={href} title="Open client" style={{ color: work.tealBlue, fontSize: '0.9rem', textDecoration: 'none', flexShrink: 0 }}>
          <i className="bi bi-box-arrow-up-right" />
        </a>
      )}
    </div>
  );
}
FolderRow.propTypes = { node: PropTypes.object.isRequired, isOpen: PropTypes.bool, onToggle: PropTypes.func.isRequired, clientHref: PropTypes.func };

// Leaf: a single file at the deepest level of the tree.
function TreeFile({ doc, onDownload, onView, onSetYear }) {
  const name = doc.display_label || doc.doc_label || doc.original_filename;
  return (
    <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.4rem 0.4rem' }}>
      <i className={`bi bi-${iconForType(doc.content_type, doc.original_filename)}`} style={{ color: work.tealBlue, fontSize: '1.1rem', marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: work.ink, wordBreak: 'break-word' }}>{name}</span>
          {doc.original_filename && doc.original_filename !== name && (
            <span style={{ fontSize: '0.72rem', color: work.muted }}>{doc.original_filename}</span>
          )}
        </div>
        <DocMeta doc={doc} onSetYear={onSetYear} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', flexShrink: 0 }}>
        <StatusPill status={doc.status} />
        <span style={{ fontSize: '0.72rem', color: work.muted }}>{fmtDate(doc.uploaded_at)}</span>
        <FileActions doc={doc} onDownload={onDownload} onView={onView} />
        {doc.file_size_bytes ? <span style={{ fontSize: '0.68rem', color: work.muted }}>{fmtBytes(doc.file_size_bytes)}</span> : null}
      </div>
    </div>
  );
}
TreeFile.propTypes = { doc: PropTypes.object.isRequired, onDownload: PropTypes.func, onView: PropTypes.func, onSetYear: PropTypes.func };

function TreeNode({ node, expanded, onToggle, onDownload, onView, clientHref, onSetYear }) {
  const isOpen = expanded.has(node.id);
  return (
    <div>
      <FolderRow node={node} isOpen={isOpen} onToggle={() => onToggle(node.id)} clientHref={clientHref} />
      {isOpen && (
        <div style={{ marginLeft: 17, borderLeft: `1px solid ${work.hairline}`, paddingLeft: 6 }}>
          {node.children.map((child) => (
            child.kind === 'file'
              ? <TreeFile key={child.id} doc={child.doc} onDownload={onDownload} onView={onView} onSetYear={onSetYear} />
              : <TreeNode key={child.id} node={child} expanded={expanded} onToggle={onToggle} onDownload={onDownload} onView={onView} clientHref={clientHref} onSetYear={onSetYear} />
          ))}
        </div>
      )}
    </div>
  );
}
TreeNode.propTypes = { node: PropTypes.object.isRequired, expanded: PropTypes.instanceOf(Set).isRequired, onToggle: PropTypes.func.isRequired, onDownload: PropTypes.func, onView: PropTypes.func, clientHref: PropTypes.func, onSetYear: PropTypes.func };

function FolderView({ documents, onDownload, onView, clientHref, onSetYear }) {
  const tree = useMemo(() => buildFolderTree(documents), [documents]);
  const allFolderIds = useMemo(() => collectFolderIds(tree), [tree]);

  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => setExpanded(new Set(allFolderIds));
  const collapseAll = () => setExpanded(new Set());

  if (tree.length === 0) return null;

  const ctrlBtn = { background: 'transparent', border: `1px solid ${work.hairlineStrong}`, borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem', color: work.deepBlue, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' };

  return (
    <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.6rem', borderBottom: `1px solid ${work.hairline}`, background: work.canvas }}>
        <i className="bi bi-diagram-3" style={{ color: work.deepBlue }} />
        <span style={{ fontSize: '0.76rem', fontWeight: 800, color: work.deepBlue, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Folder tree</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <button type="button" onClick={expandAll} style={ctrlBtn}><i className="bi bi-arrows-expand me-1" />Expand all</button>
          <button type="button" onClick={collapseAll} style={ctrlBtn}><i className="bi bi-arrows-collapse me-1" />Collapse all</button>
        </div>
      </div>
      <div style={{ padding: '0.4rem 0.5rem' }}>
        {tree.map((node) => (
          <TreeNode key={node.id} node={node} expanded={expanded} onToggle={toggle} onDownload={onDownload} onView={onView} clientHref={clientHref} onSetYear={onSetYear} />
        ))}
      </div>
    </div>
  );
}
FolderView.propTypes = { documents: PropTypes.array.isRequired, onDownload: PropTypes.func, onView: PropTypes.func, clientHref: PropTypes.func, onSetYear: PropTypes.func };

export default function DocumentBrowser({
  documents, facets, total, filtered, loading, error,
  params, onParamsChange, onDownload, onView, clientHref, showFirmFilter, showAdvisorFilter,
  showYearFilter, onSetYear, title, subtitle,
}) {
  const [view, setView] = useState('folder'); // 'list' | 'folder'
  const [qDraft, setQDraft] = useState(params.q || '');

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => { setQDraft(params.q || ''); }, [params.q]);
  useEffect(() => {
    const id = setTimeout(() => {
      if ((qDraft || '') !== (params.q || '')) onParamsChange({ q: qDraft || undefined });
    }, 300);
    return () => clearTimeout(id);
  }, [qDraft, params.q, onParamsChange]);

  const set = (patch) => onParamsChange(patch);
  const activeLetter = params.letter || '';
  const hasFilters = Boolean(
    params.q || params.type_key || params.category || params.status || params.institution ||
    params.advisor || params.workflow_status || params.firm_id || params.letter ||
    params.date_from || params.date_to || params.document_year,
  );
  const clearAll = () => onParamsChange({
    q: undefined, type_key: undefined, category: undefined, status: undefined,
    institution: undefined, advisor: undefined, workflow_status: undefined,
    firm_id: undefined, letter: undefined, date_from: undefined, date_to: undefined,
    document_year: undefined,
  });

  const letterSet = useMemo(() => new Set(facets?.letters || []), [facets]);
  const f = facets || {};

  const tabBtn = (key, label, icon) => (
    <button
      type="button"
      onClick={() => setView(key)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.45rem 0.9rem', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
        border: `1px solid ${work.hairlineStrong}`, background: view === key ? work.deepBlue : work.surface,
        color: view === key ? 'white' : work.deepBlue,
        borderRadius: key === 'list' ? '6px 0 0 6px' : '0 6px 6px 0',
        borderRight: key === 'list' ? 'none' : `1px solid ${work.hairlineStrong}`,
      }}
    >
      <i className={`bi bi-${icon}`} />{label}
    </button>
  );

  return (
    <div>
      {(title || subtitle) && (
        <div style={{ marginBottom: '1rem' }}>
          {title && <h3 style={{ color: work.deepBlue, fontWeight: 800, margin: 0 }}>{title}</h3>}
          {subtitle && <p style={{ color: work.muted, margin: '0.15rem 0 0', fontSize: '0.875rem' }}>{subtitle}</p>}
        </div>
      )}

      {/* Toolbar: search + view tabs + sort */}
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 220 }}>
          <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: work.muted, fontSize: '0.85rem' }} />
          <input
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder="Search client, advisor, institution, account, holder, filename…"
            style={{ width: '100%', padding: '0.5rem 0.6rem 0.5rem 2rem', border: `1px solid ${work.hairlineStrong}`, borderRadius: 6, fontSize: '0.85rem', color: work.ink, outline: 'none' }}
          />
        </div>
        <div style={{ display: 'flex' }}>
          {tabBtn('list', 'List', 'list-ul')}
          {tabBtn('folder', 'Folders', 'folder2')}
        </div>
        <select value={params.sort || 'recent'} onChange={(e) => set({ sort: e.target.value })} aria-label="Sort" style={selectStyle()}>
          <option value="recent">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="client">By client (A–Z)</option>
        </select>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {showFirmFilter && (f.firms?.length || 0) > 0 && (
          <select value={params.firm_id || ''} onChange={(e) => set({ firm_id: e.target.value ? Number(e.target.value) : undefined })} aria-label="Firm" title="Firm" style={selectStyle()}>
            <option value="">All firms</option>
            {f.firms.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
        {showAdvisorFilter && (f.advisors?.length || 0) > 0 && (
          <select value={params.advisor || ''} onChange={(e) => set({ advisor: e.target.value || undefined })} aria-label="Advisor" title="Advisor" style={selectStyle()}>
            <option value="">All advisors</option>
            {f.advisors.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        {(f.categories?.length || 0) > 0 && (
          <select value={params.category || ''} onChange={(e) => set({ category: e.target.value || undefined, type_key: undefined })} aria-label="Category" title="Document category" style={selectStyle()}>
            <option value="">All categories</option>
            {f.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        )}
        {(f.doc_types?.length || 0) > 0 && (
          <select value={params.type_key || ''} onChange={(e) => set({ type_key: e.target.value || undefined })} aria-label="Document type" title="Document type" style={selectStyle()}>
            <option value="">All document types</option>
            {f.doc_types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        )}
        {(f.institutions?.length || 0) > 0 && (
          <select value={params.institution || ''} onChange={(e) => set({ institution: e.target.value || undefined })} aria-label="Institution" title="Financial institution" style={selectStyle()}>
            <option value="">All institutions</option>
            {f.institutions.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        )}
        <select value={params.status || ''} onChange={(e) => set({ status: e.target.value || undefined })} aria-label="Status" title="Status" style={selectStyle()}>
          <option value="">Any status</option>
          <option value="uploaded">Received</option>
          <option value="pending">Pending</option>
        </select>
        {(f.workflow_stages?.length || 0) > 0 && (
          <select value={params.workflow_status || ''} onChange={(e) => set({ workflow_status: e.target.value || undefined })} aria-label="Workflow stage" title="Client workflow stage" style={selectStyle()}>
            <option value="">Any stage</option>
            {f.workflow_stages.map((w) => <option key={w} value={w}>{WORKFLOW_LABELS[w] || w}</option>)}
          </select>
        )}
        <input type="date" value={params.date_from || ''} max={params.date_to || undefined} onChange={(e) => set({ date_from: e.target.value || undefined })} title="Uploaded from" style={selectStyle()} />
        <input type="date" value={params.date_to || ''} min={params.date_from || undefined} onChange={(e) => set({ date_to: e.target.value || undefined })} title="Uploaded to" style={selectStyle()} />
        {showYearFilter && (f.document_years?.length || 0) > 0 && (
          <select value={params.document_year || ''} onChange={(e) => set({ document_year: e.target.value ? Number(e.target.value) : undefined })} aria-label="Document year" title="Which year the document is about" style={selectStyle()}>
            <option value="">Any year</option>
            {f.document_years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {hasFilters && (
          <button type="button" onClick={clearAll} style={{ background: 'transparent', border: `1px solid ${work.hairlineStrong}`, borderRadius: 6, padding: '0.4rem 0.6rem', fontSize: '0.82rem', color: work.deepBlue, cursor: 'pointer', fontWeight: 700 }}>
            <i className="bi bi-x-circle me-1" />Clear
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: work.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {loading ? 'Loading…' : `${filtered}${filtered !== total ? ` of ${total}` : ''} document${total === 1 ? '' : 's'}`}
        </span>
      </div>

      {/* Alphabetical jump strip (by client last name) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: '0.85rem' }}>
        <button
          type="button"
          onClick={() => set({ letter: undefined })}
          style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem', fontWeight: 700, border: `1px solid ${work.hairline}`, borderRadius: 4, cursor: 'pointer', background: !activeLetter ? work.deepBlue : work.surface, color: !activeLetter ? 'white' : work.deepBlue }}
        >
          All
        </button>
        {LETTERS.map((L) => {
          const present = letterSet.has(L);
          const active = activeLetter === L;
          return (
            <button
              key={L}
              type="button"
              disabled={!present}
              onClick={() => set({ letter: active ? undefined : L })}
              style={{
                width: 26, padding: '0.15rem 0', fontSize: '0.75rem', fontWeight: 700, borderRadius: 4,
                border: `1px solid ${work.hairline}`, cursor: present ? 'pointer' : 'default',
                background: active ? work.deepBlue : work.surface,
                color: active ? 'white' : (present ? work.deepBlue : work.hairlineStrong),
              }}
            >
              {L}
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ background: 'rgba(176,0,32,0.06)', border: `1px solid ${work.red}`, color: work.red, borderRadius: 6, padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}><div className="spinner-border" style={{ color: work.tealBlue }} /></div>
      ) : (documents.length === 0) ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: work.muted, background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6 }}>
          <i className="bi bi-folder-x" style={{ fontSize: '2rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
          {hasFilters ? 'No documents match these filters.' : 'No client documents yet.'}
        </div>
      ) : view === 'list' ? (
        <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, overflow: 'hidden' }}>
          {documents.map((d) => <DocRow key={docKey(d)} doc={d} onDownload={onDownload} onView={onView} clientHref={clientHref} onSetYear={onSetYear} />)}
        </div>
      ) : (
        <FolderView documents={documents} onDownload={onDownload} onView={onView} clientHref={clientHref} onSetYear={onSetYear} />
      )}
    </div>
  );
}

DocumentBrowser.propTypes = {
  documents: PropTypes.array,
  facets: PropTypes.object,
  total: PropTypes.number,
  filtered: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  params: PropTypes.object.isRequired,
  onParamsChange: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onView: PropTypes.func,
  clientHref: PropTypes.func,
  showFirmFilter: PropTypes.bool,
  showAdvisorFilter: PropTypes.bool,
  showYearFilter: PropTypes.bool,
  onSetYear: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

DocumentBrowser.defaultProps = {
  documents: [], facets: {}, total: 0, filtered: 0, loading: false,
  error: null, onView: null, clientHref: null, showFirmFilter: false, showAdvisorFilter: true,
  showYearFilter: false, onSetYear: null,
  title: null, subtitle: null,
};
